import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, ExternalLink, Trash2, AlertTriangle, Zap, Plus } from "lucide-react";
import { toast } from "sonner";
import { getStripeEnvironment } from "@/lib/stripe";
import { getLinkActivationDiagnostics } from "@/lib/activation-diagnostics.functions";
import { activateLinkManually, createLinkManually } from "@/lib/admin-activation.functions";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/admin/odkazy")({ component: AdminLinksPage });

type Status = "pending_payment" | "active" | "rejected";
const statusLabel: Record<Status, string> = {
  pending_payment: "Čeká na platbu",
  active: "Aktivní",
  rejected: "Zamítnutý",
};
const statusVariant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  pending_payment: "outline",
  active: "default",
  rejected: "destructive",
};

function AdminLinksPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"all" | Status>("all");

  const { data: links = [], isLoading } = useQuery({
    queryKey: ["admin", "links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_links")
        .select("id, url, label, is_free, status, paid_at, created_at, price_cents_at_purchase, currency_at_purchase, business:business_id(id, name, owner_id)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const pendingCount = links.filter((l) => l.status === "pending_payment").length;
  const { data: diagnostics = [] } = useQuery({
    queryKey: ["admin", "links", "activation-diagnostics", pendingCount],
    queryFn: () =>
      getLinkActivationDiagnostics({ data: { environment: getStripeEnvironment() } }),
    enabled: pendingCount > 0,
    staleTime: 30_000,
  });
  const diagByLink = useMemo(
    () => new Map(diagnostics.map((d) => [d.refId, d])),
    [diagnostics],
  );

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<{ status: Status; paid_at: string | null }> }) => {
      const { error } = await supabase.from("business_links").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "links"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("business_links").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "links"] });
      toast.success("Odkaz smazán.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // --- Ruční aktivace reklamního odkazu (když webhook předplatné zmeškal) ---
  const [activateFor, setActivateFor] = useState<{ id: string; name: string } | null>(null);
  const [subId, setSubId] = useState("");
  const [plan, setPlan] = useState<"monthly" | "yearly" | "lifetime">("monthly");
  const [sendEmail, setSendEmail] = useState(true);

  const activate = useMutation({
    mutationFn: async () => {
      if (!activateFor) throw new Error("Chybí odkaz.");
      const res = await activateLinkManually({
        data: {
          environment: getStripeEnvironment(),
          linkId: activateFor.id,
          ...(subId.trim() ? { stripeSubscriptionId: subId.trim() } : {}),
          plan,
          sendEmail,
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "links"] });
      toast.success(
        res.mode === "stripe"
          ? "Odkaz aktivován a spárován se Stripe předplatným."
          : "Odkaz ručně aktivován.",
      );
      setActivateFor(null);
      setSubId("");
      setPlan("monthly");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // --- Ruční přidání reklamního odkazu ---
  const [addOpen, setAddOpen] = useState(false);
  const [newBusinessId, setNewBusinessId] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newPlan, setNewPlan] = useState<"monthly" | "yearly" | "lifetime">("monthly");
  const [newSendEmail, setNewSendEmail] = useState(false);

  const { data: businesses = [] } = useQuery({
    queryKey: ["admin", "businesses", "for-links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("businesses")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: addOpen,
  });

  const createLink = useMutation({
    mutationFn: async () => {
      if (!newBusinessId) throw new Error("Vyberte podnik.");
      if (!newUrl.trim()) throw new Error("Zadejte URL odkazu.");
      const res = await createLinkManually({
        data: {
          businessId: newBusinessId,
          url: newUrl.trim(),
          ...(newLabel.trim() ? { label: newLabel.trim() } : {}),
          plan: newPlan,
          sendEmail: newSendEmail,
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "links"] });
      toast.success("Reklamní odkaz přidán a aktivován.");
      setAddOpen(false);
      setNewBusinessId("");
      setNewUrl("");
      setNewLabel("");
      setNewPlan("monthly");
    },
    onError: (e: Error) => toast.error(e.message),
  });



  const filtered = useMemo(
    () => links.filter((l) => tab === "all" || l.status === tab),
    [links, tab],
  );

  const counts = useMemo(() => ({
    all: links.length,
    pending_payment: links.filter((l) => l.status === "pending_payment").length,
    active: links.filter((l) => l.status === "active").length,
    rejected: links.filter((l) => l.status === "rejected").length,
  }), [links]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl">Odkazy podniků</h1>
          <p className="text-sm text-muted-foreground">
            Přehled všech odkazů. Bezplatné (directclub) jsou publikovány automaticky; externí čekají na úhradu.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> Přidat odkaz ručně
        </Button>
      </div>


      <Tabs value={tab} onValueChange={(v) => setTab(v as "all" | Status)}>
        <TabsList>
          <TabsTrigger value="all">Vše ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending_payment">Čekající na platbu ({counts.pending_payment})</TabsTrigger>
          <TabsTrigger value="active">Aktivní ({counts.active})</TabsTrigger>
          <TabsTrigger value="rejected">Zamítnuté ({counts.rejected})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Podnik</TableHead>
                <TableHead>Odkaz</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Stav</TableHead>
                <TableHead>Platba</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Načítám…</TableCell></TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Žádné odkazy.</TableCell></TableRow>
              )}
              {filtered.map((l) => {
                const biz = l.business as { name: string } | null;
                return (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">{biz?.name ?? "—"}</TableCell>
                    <TableCell className="max-w-xs">
                      <a href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline truncate">
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{l.label || l.url}</span>
                      </a>
                      {l.label && <div className="text-xs text-muted-foreground truncate">{l.url}</div>}
                    </TableCell>
                    <TableCell>
                      {l.is_free
                        ? <Badge variant="secondary">Zdarma (directclub)</Badge>
                        : <Badge variant="outline">Placený</Badge>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[l.status as Status]}>{statusLabel[l.status as Status]}</Badge>
                      {l.status === "pending_payment" && diagByLink.get(l.id) && (
                        <div
                          className={`mt-1 flex max-w-xs items-start gap-1 text-xs ${
                            diagByLink.get(l.id)!.severity === "error"
                              ? "text-destructive"
                              : "text-muted-foreground"
                          }`}
                        >
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                          <span>{diagByLink.get(l.id)!.reason}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {l.paid_at
                        ? `${((l.price_cents_at_purchase ?? 0) / 100).toFixed(2)} ${l.currency_at_purchase ?? ""} · ${new Date(l.paid_at).toLocaleDateString("cs-CZ")}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        {l.status !== "active" && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setActivateFor({ id: l.id, name: biz?.name ?? "—" })}
                          >
                            <Zap className="mr-1 h-4 w-4" /> Ruční aktivace
                          </Button>
                        )}
                        {l.status === "pending_payment" && (
                          <>
                            <Button size="sm" onClick={() => {
                              update.mutate({ id: l.id, patch: { status: "active", paid_at: new Date().toISOString() } });
                              toast.success("Označeno jako uhrazené a aktivováno.");
                            }}>
                              <Check className="mr-1 h-4 w-4" /> Označit jako uhrazené
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => update.mutate({ id: l.id, patch: { status: "rejected" } })}>
                              <X className="mr-1 h-4 w-4" /> Zamítnout
                            </Button>
                          </>
                        )}
                        {l.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => update.mutate({ id: l.id, patch: { status: "rejected" } })}>
                            <X className="mr-1 h-4 w-4" /> Skrýt
                          </Button>
                        )}
                        {l.status === "rejected" && (
                          <Button size="sm" variant="outline" onClick={() => update.mutate({ id: l.id, patch: { status: "active" } })}>
                            <Check className="mr-1 h-4 w-4" /> Obnovit
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => del.mutate(l.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!activateFor} onOpenChange={(o) => !o && setActivateFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ruční aktivace reklamního odkazu</DialogTitle>
            <DialogDescription>
              {activateFor?.name} — použijte, když platba proběhla, ale automatické potvrzení
              nedorazilo. Aktivace se zapíše do historie i k zákazníkovi.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="subid">ID předplatného ve Stripe (volitelné)</Label>
              <Input
                id="subid"
                placeholder="sub_..."
                value={subId}
                onChange={(e) => setSubId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Když ho vyplníte, načteme platné období přímo ze Stripe a odkaz s předplatným spárujeme.
              </p>
            </div>

            {!subId.trim() && (
              <div className="space-y-1.5">
                <Label>Období</Label>
                <Select value={plan} onValueChange={(v) => setPlan(v as typeof plan)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Měsíční (30 dní)</SelectItem>
                    <SelectItem value="yearly">Roční (365 dní)</SelectItem>
                    <SelectItem value="lifetime">Trvalý (bez expirace)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor="sendmail" className="text-sm font-normal">
                Poslat zákazníkovi potvrzení e-mailem
              </Label>
              <Switch id="sendmail" checked={sendEmail} onCheckedChange={setSendEmail} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateFor(null)}>Zrušit</Button>
            <Button onClick={() => activate.mutate()} disabled={activate.isPending}>
              {activate.isPending ? "Aktivuji…" : "Aktivovat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ruční přidání reklamního odkazu</DialogTitle>
            <DialogDescription>
              Odkaz se vytvoří rovnou jako aktivní a zobrazí se majiteli na stránce Moje podniky.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Podnik</Label>
              <Select value={newBusinessId} onValueChange={setNewBusinessId}>
                <SelectTrigger><SelectValue placeholder="Vyberte podnik" /></SelectTrigger>
                <SelectContent>
                  {businesses.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="newurl">URL odkazu</Label>
              <Input id="newurl" placeholder="https://…" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="newlabel">Popisek (volitelné)</Label>
              <Input id="newlabel" placeholder="Např. Web podniku" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label>Platnost</Label>
              <Select value={newPlan} onValueChange={(v) => setNewPlan(v as typeof newPlan)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Měsíční (30 dní)</SelectItem>
                  <SelectItem value="yearly">Roční (365 dní)</SelectItem>
                  <SelectItem value="lifetime">Trvalá (bez expirace)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor="newsendmail" className="text-sm font-normal">
                Poslat majiteli potvrzení e-mailem
              </Label>
              <Switch id="newsendmail" checked={newSendEmail} onCheckedChange={setNewSendEmail} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Zrušit</Button>
            <Button onClick={() => createLink.mutate()} disabled={createLink.isPending}>
              {createLink.isPending ? "Přidávám…" : "Přidat a aktivovat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
}
